import React from 'react';
import { Checkbox, useTheme, spacing } from '@fti/design-system';
import { DataGridCell } from './DataGridCell';

interface DataGridRowProps {
  rowIndex: number;
  values: (string | number | null)[];
  columns: string[];
  isDeleted: boolean;
  isSelected: boolean;
  isNewRow: boolean;
  activeCell: { row: number; col: number } | null;
  editingCell: { row: number; col: number } | null;
  isCellDirty: (rowIndex: number, columnIndex: number) => boolean;
  getCellValue: (rowIndex: number, columnIndex: number) => string | number | null | undefined;
  isColumnDeleted: (columnIndex: number) => boolean;
  onSelectRow: (rowIndex: number, selected: boolean) => void;
  onActivate: (rowIndex: number, columnIndex: number) => void;
  onStartEdit: (rowIndex: number, columnIndex: number) => void;
  onCommitEdit: (rowIndex: number, columnIndex: number, value: string | number | null) => void;
  onCancelEdit: () => void;
  onNavigate: (direction: 'up' | 'down' | 'left' | 'right') => void;
}

export const DataGridRow: React.FC<DataGridRowProps> = ({
  rowIndex,
  values,
  columns,
  isDeleted,
  isSelected,
  isNewRow,
  activeCell,
  editingCell,
  isCellDirty,
  getCellValue,
  isColumnDeleted,
  onSelectRow,
  onActivate,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onNavigate,
}) => {
  const { theme } = useTheme();

  const rowStyle: React.CSSProperties = {
    borderBottom: `1px solid ${theme.colors.border.default}`,
    backgroundColor: isNewRow
      ? `${theme.colors.semantic.success.light}10`
      : isDeleted
        ? `${theme.colors.semantic.error.light}08`
        : rowIndex % 2 === 0
          ? undefined
          : theme.colors.neutral[50],
  };

  return (
    <tr style={rowStyle} role="row" aria-rowindex={rowIndex + 1}>
      <td style={{ padding: spacing.sm, width: '40px', textAlign: 'center' }}>
        <Checkbox
          checked={isSelected}
          onChange={(e) => onSelectRow(rowIndex, (e.target as HTMLInputElement).checked)}
          disabled={isDeleted}
          aria-label={`Select row ${rowIndex + 1}`}
        />
      </td>
      {columns.map((_, colIdx) => {
        if (isColumnDeleted(colIdx)) return null;

        const draftValue = getCellValue(rowIndex, colIdx);
        const displayValue = draftValue !== undefined ? draftValue : (values[colIdx] ?? null);
        const dirty = isCellDirty(rowIndex, colIdx);
        const isActive = activeCell?.row === rowIndex && activeCell?.col === colIdx;
        const isEditing = editingCell?.row === rowIndex && editingCell?.col === colIdx;

        return (
          <DataGridCell
            key={colIdx}
            value={displayValue}
            rowIndex={rowIndex}
            columnIndex={colIdx}
            isDirty={dirty}
            isDeleted={isDeleted || isColumnDeleted(colIdx)}
            isActive={isActive}
            isEditing={isEditing}
            onActivate={onActivate}
            onStartEdit={onStartEdit}
            onCommitEdit={onCommitEdit}
            onCancelEdit={onCancelEdit}
            onNavigate={onNavigate}
          />
        );
      })}
    </tr>
  );
};
